import Recaptcha from '@components/App/RecaptchaWidget'
import { Input } from '@lib/components'
import { Radio } from '@lib/components/Radio'
import { UploadFile } from '@lib/components/UploadFile'
import { IconWorld } from '@lib/icons'
import { PagesOnly, WithMetaTags } from '@lib/images'
import { isValidIDNUrl } from '@lib/utils/strings'
import { Button } from '@octopus-do/octopus-storybook'
import type React from 'react'
import { type MutableRefObject, useEffect, useState } from 'react'
import { useMount } from 'react-use'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useFeatureFlags } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { useSitemap } from '../../../hooks'
import globalCss from '../../../Import.module.scss'
import { LimitNotice } from '../../LimitNotice'
import { Selectors } from '../../Selectors'
import { SELECTORS } from '../constants'
import css from './SitemapTab.module.scss'

const SITEMAPS = {
  FROM_URL: 'FROM_URL',
  UPLOAD_FILE: 'UPLOAD_FILE',
}

type Props = {
  url: string
  setUrl: (url: string) => void
  activeSelector: string
  setActiveSelector: (activeSelector: string) => void
  teamId?: string
  folderId?: string | null
  // Addressed by tab id, not position: the editor renders a different set of tabs.
  setActiveTabAndUrl: (tabId: 'crawler', url: string) => void
  immediatelyRef: MutableRefObject<boolean>
  // Land the pages in the project the user already has open.
  intoOpenProject?: boolean
}

const SitemapTab: React.FC<Props> = ({
  url,
  setUrl,
  teamId,
  folderId,
  setActiveTabAndUrl,
  activeSelector,
  setActiveSelector,
  immediatelyRef,
  intoOpenProject = false,
}) => {
  const [sitemapType, setSitemapType] = useState(SITEMAPS.FROM_URL)
  const [file, setFile] = useState<File | null>(null)

  const [inputError, setInputError] = useState(false)
  const [loading, setLoading] = useState(false)

  const {
    crawler: { userHasMaximumLimit },
  } = useFeatureFlags()
  const metaTagsLimited =
    !userHasMaximumLimit &&
    sitemapType === SITEMAPS.FROM_URL &&
    activeSelector === SELECTORS.WITH_META_TAGS

  const handleChangeOption = ({ sitemap }: { sitemap: string }) => {
    setInputError(false)
    setError('')
    setSitemapType(sitemap)
  }

  const { error, setError, runFromUrls, runFromFile } = useSitemap({
    intoOpenProject,
    teamId,
    folderId,
    metaTag: activeSelector === SELECTORS.WITH_META_TAGS || false,
  })

  const handleSubmitWithUrl = () => {
    setLoading(false)
    setInputError(false)

    if (!isValidIDNUrl(url)) {
      setInputError(true)
      return
    }

    setLoading(true)
    setInputError(false)

    runFromUrls({
      urls: [url],
    })
      .then(() => {})
      .catch((err) => console.error(err))
  }

  useMount(() => {
    if (immediatelyRef.current) {
      handleSubmitWithUrl()
      immediatelyRef.current = false
    }
  })

  const handleSubmitWithFile = () => {
    if (!file) {
      setInputError(true)
      return
    }

    setLoading(true)
    setInputError(false)

    runFromFile({
      file,
    })
  }

  // The open project takes no pages while the artboard is off, so the import
  // would end in an error (see useCommitIntoProject).
  const artboardDisabled = useArtBoardDisabled()
  const importDisabled = intoOpenProject && artboardDisabled

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (importDisabled) {
      return
    }

    if (sitemapType === SITEMAPS.FROM_URL) {
      sendGtmEvent(
        activeSelector === SELECTORS.WITH_META_TAGS
          ? GtmTriggerName.CRAWL_SITEMAP_SEO
          : GtmTriggerName.CRAWL_SITEMAP_PAGES
      )
      handleSubmitWithUrl()
    } else {
      sendGtmEvent(GtmTriggerName.CRAWL_SITEMAP_PAGES)
      handleSubmitWithFile()
    }
  }

  useEffect(() => {
    if (error) {
      setLoading(false)
    }
  }, [error, loading])

  return (
    <Recaptcha>
      <form onSubmit={handleSubmit}>
        <p className={globalCss.description}>
          The crawler allows you to import an exact sitemap, but the import can
          take a several minutes.
        </p>

        <div className={css.radios}>
          <Radio
            name="sitemap"
            initialValue={SITEMAPS.FROM_URL}
            label="From URL"
            defaultChecked
            classnames={{
              container: css.radio,
            }}
            onChange={handleChangeOption as never}
          />
          <Radio
            name="sitemap"
            initialValue={SITEMAPS.UPLOAD_FILE}
            label="Upload File"
            classnames={{
              container: css.radio,
            }}
            onChange={handleChangeOption as never}
          />
        </div>

        {sitemapType === SITEMAPS.FROM_URL && (
          <div className={css.input}>
            <Input
              placeholder="https://website.com"
              type="normal"
              autoComplete="off"
              spellCheck="false"
              initialValue={url}
              icon={<IconWorld />}
              onChange={(value) => setUrl(value)}
              error={inputError ? 'Please enter a valid URL' : undefined}
            />
          </div>
        )}

        {sitemapType === SITEMAPS.UPLOAD_FILE && (
          <div className={css.select}>
            <UploadFile
              accept="text/xml"
              label="Select XML"
              priority="secondary"
              onChange={(file: File) => setFile(file)}
            />
          </div>
        )}

        {sitemapType === SITEMAPS.FROM_URL && (
          <Selectors
            items={[
              {
                id: SELECTORS.PAGES_ONLY,
                label: 'Pages only',
                icon: <PagesOnly />,
                badge: null,
              },
              {
                id: SELECTORS.WITH_META_TAGS,
                label: 'With meta tags',
                icon: <WithMetaTags />,
                badge: null,
              },
            ]}
            onChange={(value) => {
              setActiveSelector(value)
            }}
            activeItemId={activeSelector}
          />
        )}

        {metaTagsLimited && (
          <LimitNotice>Free plan limit for meta tags: 300 pages</LimitNotice>
        )}

        <div className={globalCss.button}>
          <Button
            label="Import"
            loading={loading}
            disabled={importDisabled}
            onClick={handleSubmit}
          />
        </div>

        <div className={globalCss.error}>
          {error && <span className={globalCss.errorText}>{error}</span>}
          {sitemapType === SITEMAPS.FROM_URL && error && (
            <>
              <span
                className={globalCss.link}
                onClick={() => {
                  setActiveTabAndUrl('crawler', url)
                }}
              >
                You can crawl this website
              </span>{' '}
              to create its sitemap.
            </>
          )}
        </div>
      </form>
    </Recaptcha>
  )
}

export default SitemapTab
